import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Ship, Plane, ArrowLeft, DollarSign, Calendar, Upload, 
  Anchor, AlertCircle, Building2, ImagePlus, X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import Header from '../components/Header';

const API = process.env.REACT_APP_BACKEND_URL;

const vesselTypes = [
  { value: 'container', label: 'Container Ship' },
  { value: 'bulk_carrier', label: 'Bulk Carrier' },
  { value: 'tanker', label: 'Tanker' },
  { value: 'roro', label: 'RoRo' },
  { value: 'tug', label: 'Tug' },
  { value: 'barge', label: 'Barge' },
  { value: 'general_cargo', label: 'General Cargo' },
  { value: 'reefer', label: 'Reefer' },
  { value: 'chemical_tanker', label: 'Chemical Tanker' },
  { value: 'oil_tanker', label: 'Oil Tanker' },
  { value: 'lpg_carrier', label: 'LPG Carrier' },
  { value: 'lng_carrier', label: 'LNG Carrier' },
  { value: 'car_carrier', label: 'Car Carrier' },
  { value: 'heavy_lift', label: 'Heavy Lift' },
  { value: 'offshore_supply', label: 'Offshore Supply' },
  { value: 'multipurpose', label: 'Multipurpose'  }
];

const rateUnits = [
  { value: 'lumpsum', label: 'Lump Sum' },
  { value: 'per_mt', label: 'Per Metric Ton' },
  { value: 'per_day', label: 'Per Day' },
  { value: 'per_cbm', label: 'Per CBM' },
  { value: 'per_teu', label: 'Per TEU' }
];

const CreateCharterQuote = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  
  const [rfq, setRfq] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [fleet, setFleet] = useState([]);
  const [selectedVessel, setSelectedVessel] = useState(null);
  const [uploadingImages, setUploadingImages] = useState(false);
  
  const [formData, setFormData] = useState({
    // Vessel details
    vessel_name: '',
    vessel_imo: '',
    vessel_type: '',
    vessel_flag: '',
    vessel_year_built: '',
    vessel_dwt: '',
    vessel_photos: [],
    // Aircraft details (for cargo flights)
    aircraft_type: '',
    aircraft_registration: '',
    aircraft_photos: [],
    // Quote details
    freight_rate: '',
    rate_unit: 'lumpsum',
    currency: 'USD',
    available_from: '',
    estimated_voyage_days: '',
    payment_terms: '',
    validity_days: '7',
    notes: ''
  });

  useEffect(() => {
    fetchRFQDetails();
    fetchFleet();
  }, [id]);

  const fetchRFQDetails = async () => {
    try {
      const res = await fetch(`${API}/api/charter-rfqs/${id}`);
      if (res.ok) {
        const data = await res.json();
        setRfq(data);
        if (data.status !== 'open') {
          toast.error('This charter request is no longer accepting quotes');
          navigate(`/charters/${id}`);
        }
      } else {
        toast.error('Charter request not found');
        navigate('/charters');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to load charter request');
    } finally {
      setLoading(false);
    }
  };

  const fetchFleet = async () => {
    try {
      const res = await fetch(`${API}/api/fleet`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setFleet(data);
      }
    } catch (error) {
      console.error('Error fetching fleet:', error);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSelectVessel = (vesselId) => {
    const vessel = fleet.find(v => v.id === vesselId);
    if (vessel) {
      setSelectedVessel(vessel);
      setFormData(prev => ({
        ...prev,
        vessel_name: vessel.name,
        vessel_imo: vessel.imo_number,
        vessel_type: vessel.vessel_type,
        vessel_flag: vessel.flag,
        vessel_year_built: vessel.year_built?.toString() || '',
        vessel_dwt: vessel.dwt?.toString() || '',
        vessel_photos: vessel.photos || []
      }));
    }
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploadingImages(true);
    const uploadedUrls = [];

    for (const file of files) {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      try {
        const res = await fetch(`${API}/api/upload`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formDataUpload
        });
        if (res.ok) {
          const data = await res.json();
          uploadedUrls.push(data.url);
        }
      } catch (error) {
        console.error('Upload error:', error);
      }
    }

    if (rfq?.charter_type === 'vessel') {
      setFormData(prev => ({
        ...prev,
        vessel_photos: [...prev.vessel_photos, ...uploadedUrls]
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        aircraft_photos: [...prev.aircraft_photos, ...uploadedUrls]
      }));
    }
    
    setUploadingImages(false);
    if (uploadedUrls.length > 0) {
      toast.success(`${uploadedUrls.length} image(s) uploaded`);
    }
  };

  const removePhoto = (index, type) => {
    if (type === 'vessel') {
      setFormData(prev => ({
        ...prev,
        vessel_photos: prev.vessel_photos.filter((_, i) => i !== index)
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        aircraft_photos: prev.aircraft_photos.filter((_, i) => i !== index)
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (rfq?.charter_type === 'vessel') {
      if (!formData.vessel_name || !formData.vessel_imo) {
        toast.error('Vessel name and IMO number are required');
        return;
      }
      if (formData.vessel_photos.length === 0) {
        toast.error('At least one vessel photo is required');
        return;
      }
    } else {
      if (!formData.aircraft_type) {
        toast.error('Aircraft type is required');
        return;
      }
      if (formData.aircraft_photos.length === 0) {
        toast.error('At least one aircraft photo is required');
        return;
      }
    }

    if (!formData.freight_rate) {
      toast.error('Please enter a freight rate');
      return;
    }

    setSubmitting(true);
    try {
      const submitData = {
        charter_rfq_id: id,
        ...formData,
        freight_rate: parseFloat(formData.freight_rate),
        vessel_year_built: formData.vessel_year_built ? parseInt(formData.vessel_year_built) : null,
        vessel_dwt: formData.vessel_dwt ? parseFloat(formData.vessel_dwt) : null,
        estimated_voyage_days: formData.estimated_voyage_days ? parseInt(formData.estimated_voyage_days) : null,
        validity_days: parseInt(formData.validity_days)
      };

      const res = await fetch(`${API}/api/charter-quotes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(submitData)
      });

      if (res.ok) {
        toast.success('Quote submitted successfully!');
        navigate(`/charters/${id}`);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to submit quote');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('An error occurred');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-slate-50 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
        </div>
      </>
    );
  }

  if (!rfq) {
    return null;
  }

  const isVesselCharter = rfq.charter_type === 'vessel';
  const photos = isVesselCharter ? formData.vessel_photos : formData.aircraft_photos;

  return (
    <>
      <Header />
      <div className="min-h-screen bg-slate-50 py-8">
        <div className="max-w-4xl mx-auto px-4">
          {/* Navigation */}
          <Button
            variant="ghost"
            onClick={() => navigate(`/charters/${id}`)}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Request
          </Button>

          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-slate-900">Submit Quote</h1>
            <p className="text-slate-600 mt-1">Respond to charter request: {rfq.title}</p>
          </div>

          {/* RFQ Summary */}
          <Card className="mb-6 bg-slate-100">
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div className={`p-3 rounded-lg ${isVesselCharter ? 'bg-cyan-100' : 'bg-purple-100'}`}>
                  {isVesselCharter ? (
                    <Ship className="h-6 w-6 text-cyan-600" />
                  ) : (
                    <Plane className="h-6 w-6 text-purple-600" />
                  )}
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold">{rfq.title}</h3>
                  <p className="text-sm text-slate-600">
                    {rfq.origin_port} → {rfq.destination_port}
                    {rfq.cargo_quantity && ` | ${rfq.cargo_quantity.toLocaleString()} ${rfq.cargo_unit}`}
                  </p>
                </div>
                <Badge>{rfq.charter_type === 'vessel' ? 'Vessel' : 'Cargo Flight'}</Badge>
              </div>
            </CardContent>
          </Card>

          <form onSubmit={handleSubmit}>
            {/* Vessel/Aircraft Selection */}
            {isVesselCharter && fleet.length > 0 && (
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle>Select from Your Fleet</CardTitle>
                  <CardDescription>Choose a vessel from your fleet or enter details manually</CardDescription>
                </CardHeader>
                <CardContent>
                  <Select onValueChange={handleSelectVessel}>
                    <SelectTrigger data-testid="fleet-select">
                      <SelectValue placeholder="Select a vessel from your fleet" />
                    </SelectTrigger>
                    <SelectContent>
                      {fleet.map((vessel) => (
                        <SelectItem key={vessel.id} value={vessel.id}>
                          {vessel.name} - IMO: {vessel.imo_number}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </CardContent>
              </Card>
            )}

            {/* Vessel Details */}
            {isVesselCharter ? (
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Anchor className="h-5 w-5" />
                    Vessel Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Vessel Name *</Label>
                      <Input
                        value={formData.vessel_name}
                        onChange={(e) => handleChange('vessel_name', e.target.value)}
                        placeholder="e.g., MV Pacific Star"
                        required
                        data-testid="vessel-name"
                      />
                    </div>
                    <div>
                      <Label>IMO Number *</Label>
                      <Input
                        value={formData.vessel_imo}
                        onChange={(e) => handleChange('vessel_imo', e.target.value)}
                        placeholder="e.g., 9876543"
                        required
                        data-testid="vessel-imo"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Vessel Type</Label>
                      <Select
                        value={formData.vessel_type}
                        onValueChange={(v) => handleChange('vessel_type', v)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          {vesselTypes.map(type => (
                            <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Flag State</Label>
                      <Input
                        value={formData.vessel_flag}
                        onChange={(e) => handleChange('vessel_flag', e.target.value)}
                        placeholder="e.g., Panama"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Year Built</Label>
                      <Input
                        type="number"
                        value={formData.vessel_year_built}
                        onChange={(e) => handleChange('vessel_year_built', e.target.value)}
                        placeholder="e.g., 2015"
                      />
                    </div>
                    <div>
                      <Label>DWT (Deadweight Tonnage)</Label>
                      <Input
                        type="number"
                        value={formData.vessel_dwt}
                        onChange={(e) => handleChange('vessel_dwt', e.target.value)}
                        placeholder="e.g., 50000"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Plane className="h-5 w-5" />
                    Aircraft Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Aircraft Type *</Label>
                      <Input
                        value={formData.aircraft_type}
                        onChange={(e) => handleChange('aircraft_type', e.target.value)}
                        placeholder="e.g., Boeing 747-400F"
                        required
                      />
                    </div>
                    <div>
                      <Label>Registration</Label>
                      <Input
                        value={formData.aircraft_registration}
                        onChange={(e) => handleChange('aircraft_registration', e.target.value)}
                        placeholder="e.g., N12345"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Photos */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ImagePlus className="h-5 w-5" />
                  {isVesselCharter ? 'Vessel Photos *' : 'Aircraft Photos *'}
                </CardTitle>
                <CardDescription>Upload at least one photo</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-4 mb-4">
                  {photos.map((photo, idx) => (
                    <div key={idx} className="relative">
                      <img
                        src={photo}
                        alt={`Photo ${idx + 1}`}
                        className="w-32 h-24 object-cover rounded-lg border"
                      />
                      <button
                        type="button"
                        onClick={() => removePhoto(idx, isVesselCharter ? 'vessel' : 'aircraft')}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center">
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageUpload}
                    className="hidden"
                    id="photo-upload"
                    disabled={uploadingImages}
                  />
                  <label
                    htmlFor="photo-upload"
                    className="cursor-pointer"
                  >
                    {uploadingImages ? (
                      <div className="flex items-center justify-center gap-2">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-cyan-500"></div>
                        <span>Uploading...</span>
                      </div>
                    ) : (
                      <>
                        <Upload className="h-8 w-8 mx-auto text-slate-400 mb-2" />
                        <p className="text-slate-600">Click to upload photos</p>
                        <p className="text-sm text-slate-400">PNG, JPG up to 10MB each</p>
                      </>
                    )}
                  </label>
                </div>
                {photos.length === 0 && (
                  <p className="text-sm text-red-500 mt-2 flex items-center gap-1">
                    <AlertCircle className="h-4 w-4" />
                    At least one photo is required
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Quote Details */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5" />
                  Quote Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Freight Rate *</Label>
                    <Input
                      type="number"
                      value={formData.freight_rate}
                      onChange={(e) => handleChange('freight_rate', e.target.value)}
                      placeholder="e.g., 50000"
                      required
                      data-testid="freight-rate"
                    />
                  </div>
                  <div>
                    <Label>Rate Unit</Label>
                    <Select
                      value={formData.rate_unit}
                      onValueChange={(v) => handleChange('rate_unit', v)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {rateUnits.map(unit => (
                          <SelectItem key={unit.value} value={unit.value}>{unit.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Currency</Label>
                    <Select
                      value={formData.currency}
                      onValueChange={(v) => handleChange('currency', v)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="USD">USD</SelectItem>
                        <SelectItem value="EUR">EUR</SelectItem>
                        <SelectItem value="GBP">GBP</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      Available From
                    </Label>
                    <Input
                      type="date"
                      value={formData.available_from}
                      onChange={(e) => handleChange('available_from', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Estimated Voyage Duration (Days)</Label>
                    <Input
                      type="number"
                      value={formData.estimated_voyage_days}
                      onChange={(e) => handleChange('estimated_voyage_days', e.target.value)}
                      placeholder="e.g., 14"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Payment Terms</Label>
                    <Input
                      value={formData.payment_terms}
                      onChange={(e) => handleChange('payment_terms', e.target.value)}
                      placeholder="e.g., 50% upfront, 50% on delivery"
                    />
                  </div>
                  <div>
                    <Label>Quote Validity (Days)</Label>
                    <Select
                      value={formData.validity_days}
                      onValueChange={(v) => handleChange('validity_days', v)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="3">3 days</SelectItem>
                        <SelectItem value="7">7 days</SelectItem>
                        <SelectItem value="14">14 days</SelectItem>
                        <SelectItem value="30">30 days</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label>Additional Notes</Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => handleChange('notes', e.target.value)}
                    placeholder="Any additional information or terms..."
                    rows={4}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Submit */}
            <div className="flex justify-end gap-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(`/charters/${id}`)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-cyan-500 hover:bg-cyan-600"
                disabled={submitting}
                data-testid="submit-quote-form-btn"
              >
                {submitting ? 'Submitting...' : 'Submit Quote'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default CreateCharterQuote;
